import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { GenericObject, WCEnvironment } from '../types/index';
import { getExternalUserHeaders } from '../stores/externalUser.store';
import FintechAuthService from './auth/FintechAuthService';
import { BASE_URL_ENV_LOOKUP } from '../constants';

export type FintechAPICallerProps = {
  servicePath: string;
  data?: GenericObject;
  params?: GenericObject;
  extraHeaders?: Record<string, string>;
  isBlob?: boolean;
  responseType?: 'json' | 'arraybuffer';
};

export type ApiErrorDetail = {
  field: string | null;
  message: string;
  code?: string | null;
};

export interface ApiError {
  code: number;
  message: string;
  error?: string;
  details?: ApiErrorDetail[];
  flowId?: string;
  timestamp?: string;
  title?: string;
}

const PROBLEM_TYPE_INVALID_FIELDS = '/errors/invalid-fields';

class FintechAPICaller {
  private static instance: FintechAPICaller | null = null;
  private static axiosInstance = axios.create({ baseURL: 'url not initialized' });

  public initialize(environment: WCEnvironment) {
    FintechAPICaller.axiosInstance = axios.create({ baseURL: BASE_URL_ENV_LOOKUP[environment], timeout: 30000 });
    FintechAPICaller.configHeaders();
  }

  static getInstance() {
    if (!FintechAPICaller.instance) {
      FintechAPICaller.instance = new FintechAPICaller();
    }
    return FintechAPICaller.instance;
  }

  public static async post<T = GenericObject>({ servicePath, data, params, extraHeaders, responseType, isBlob }: FintechAPICallerProps): Promise<AxiosResponse<T>> {
    try {
      const config = {...this.getApiConfig(extraHeaders), params, responseType: isBlob ? 'blob' as const : responseType };
      const response: AxiosResponse<T> = await this.axiosInstance.post<T>(servicePath, data, config);
      return response;
    } catch (error) {
      await this.normalizeBlobErrorData(error);
      const apiError = this.handleError(error);
      throw apiError;
    }
  }

  public static async get<T = GenericObject>({ servicePath, params, extraHeaders }: FintechAPICallerProps): Promise<AxiosResponse<T>> {
    try {
      const config = { ...this.getApiConfig(extraHeaders), params };
      const response: AxiosResponse<T> = await this.axiosInstance.get<T>(servicePath, config);
      return response;
    } catch (error) {
      const apiError = this.handleError(error);
      throw apiError;
    }
  }

  public static async put<T = GenericObject>({ servicePath, data, extraHeaders }: FintechAPICallerProps): Promise<AxiosResponse<T>> {
    try {
      const config = this.getApiConfig(extraHeaders);
      const response: AxiosResponse<T> = await this.axiosInstance.put<T>(servicePath, data, config);
      return response;
    } catch (error) {
      const apiError = this.handleError(error);
      throw apiError;
    }
  }

  public static async delete<T = GenericObject>({ servicePath, extraHeaders }: FintechAPICallerProps): Promise<AxiosResponse<T>> {
    try {
      const config = this.getApiConfig(extraHeaders);
      const response: AxiosResponse<T> = await this.axiosInstance.delete<T>(servicePath, config);
      return response;
    } catch (error) {
      const apiError = this.handleError(error);
      throw apiError;
    }
  }

  private static isProblemDetailsInvalidFields(data: unknown): boolean {
    if (data === null || typeof data !== 'object') {
      return false;
    }
    const obj = data as Record<string, unknown>;
    if (obj.type !== PROBLEM_TYPE_INVALID_FIELDS) {
      return false;
    }
    const fields = obj.fields;
    if (fields === null || typeof fields !== 'object' || Array.isArray(fields)) {
      return false;
    }
    return Object.keys(fields).length > 0;
  }

  /**
   * No RFC 7807 (invalid-fields), `fields` é a fonte dos details: um `details` que venha no corpo é ignorado para não
   * duplicar nem divergir do que está em `fields`. `flowId` e `timestamp` são repassados para o usuário poder citá-los
   * no suporte, como nos demais formatos de erro.
   */
  private static handleInvalidFieldsError(data: any, status: number): ApiError {
    const fieldEntries = Object.entries(data.fields as Record<string, string>);

    return {
      code: status,
      title: 'Campos inválidos',
      message: fieldEntries.map(([, message]) => message).join(' | '),
      error: data.type,
      details: fieldEntries.map(([field, message]) => ({ field, message })),
      flowId: data.flowId,
      timestamp: data.timestamp,
    };
  }

  private static ensureDetails(apiError: ApiError): ApiError {
    if (Array.isArray(apiError.details) && apiError.details.length > 0) {
      return apiError;
    }
    return { ...apiError, details: [{ field: null, message: apiError.message }] };
  }

  private static async normalizeBlobErrorData(error: any): Promise<void> {
    const data = error?.response?.data;
    if (!(data instanceof Blob)) {
      return;
    }
    try {
      const text = await data.text();
      error.response.data = text ? JSON.parse(text) : undefined;
    } catch {
      error.response.data = undefined;
    }
  }
  
  private static parseErrorData(data: any): any {
    if (data instanceof ArrayBuffer) {
      try {
        return JSON.parse(new TextDecoder('utf-8').decode(data));
      } catch (err) {
        console.warn('Erro ao decodificar ArrayBuffer de erro da API.', err);
        return undefined;
      }
    }
    return data;
  }

  private static handleError(error: any): ApiError {
    if (!axios.isAxiosError(error)) {
      return this.ensureDetails({ code: 500, message: 'Erro interno' });
    }

    const status = error.response?.status || 500;
    const data = this.parseErrorData(error.response?.data);

    if (this.isProblemDetailsInvalidFields(data)) {
      return this.ensureDetails(this.handleInvalidFieldsError(data, status));
    }

    const { message: dataMessage, error: apiError, details, flowId, timestamp } =
      data && typeof data === 'object' ? data : {};

    return this.ensureDetails({
      code: status,
      message: dataMessage || error.message || 'Erro desconhecido',
      error: apiError,
      details: Array.isArray(details) ? details : undefined,
      flowId,
      timestamp,
    });
  }

  private static configHeaders() {
    FintechAPICaller.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        const tokenManager = FintechAuthService.getInstance();
        let token: string | null = null;

        if (tokenManager.shouldFetchToken(config.url!)) {
          token = await tokenManager.getAccessToken(config.url!);
        }

        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      },
    );
  }

  private static getApiConfig(extraHeaders?: GenericObject) {
    return {
      headers: {
        ...(extraHeaders || {}),
        ...getExternalUserHeaders(),
      },
    };
  }
}

export default FintechAPICaller;
